/* src/components/LegalCommandWidget.js */
import React, { useState, useMemo } from 'react';
import ReactDOM from 'react-dom'; 
import { subjectMap } from '../constants';
import './LegalCommandWidget.css';

const LegalCommandWidget = ({ allItemsMap, rulesProgress, onOpenRule, userName }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSubject, setSelectedSubject] = useState(null);
    const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);

    const subjectsWithKeynotes = useMemo(() => {
        const activeSubjs = new Set();
        Object.entries(rulesProgress || {}).forEach(([id, data]) => {
            if (data.keynote?.trim()) {
                const rule = allItemsMap.get(id);
                if (rule) activeSubjs.add(rule.subject);
            }
        });
        return Array.from(activeSubjs).sort();
    }, [rulesProgress, allItemsMap]);

    const searchResults = useMemo(() => {
        const query = searchTerm.toLowerCase().trim();
        if (!query) return [];
        const tokens = query.split(/\s+/);
        const results = [];
        
        allItemsMap.forEach((item) => {
            if (item.itemType !== 'rule') return;
            const rawKeynote = rulesProgress?.[item.id]?.keynote;
            if (!rawKeynote || !rawKeynote.trim()) return;

            const keynote = rawKeynote.toLowerCase();
            const name = item.ruleName.toLowerCase();
            const searchStack = `${name} ${keynote}`;
            
            if (tokens.every(token => searchStack.includes(token))) results.push(item);
        });
        
        return results.sort((a, b) => a.ruleName.length - b.ruleName.length).slice(0, 15);
    }, [searchTerm, allItemsMap, rulesProgress]);

    const handleOpenRule = (id) => {
        setSelectedSubject(null);
        setIsSearchModalOpen(false);
        setSearchTerm('');
        onOpenRule(id);
    };

    const outlineData = useMemo(() => {
        if (!selectedSubject) return [];
        const rules = [];
        allItemsMap.forEach((item) => {
            if (item.itemType === 'rule' && item.subject === selectedSubject) {
                const keynote = rulesProgress?.[item.id]?.keynote;
                if (keynote?.trim()) rules.push({ ...item, keynote });
            }
        });
        const groups = {};
        rules.forEach(r => {
            const unit = r.displayPath?.[0] || 'General';
            if (!groups[unit]) groups[unit] = [];
            groups[unit].push(r);
        });
        return Object.entries(groups);
    }, [selectedSubject, allItemsMap, rulesProgress]);

    // --- EXPORT LOGIC ---
    const handleExport = () => {
        if (!selectedSubject) return;

        // 1. Gather all rules for subject with keynotes
        const exportRules = [];
        allItemsMap.forEach((item) => {
            if (item.itemType === 'rule' && item.subject === selectedSubject) {
                const keynote = rulesProgress?.[item.id]?.keynote;
                if (keynote?.trim()) {
                    exportRules.push({ ...item, keynote });
                }
            }
        });

        // 2. Sort by HNU / Logic
        exportRules.sort((a, b) => {
            const hnuA = a.sortingHNU || '999';
            const hnuB = b.sortingHNU || '999';
            return hnuA.localeCompare(hnuB, undefined, { numeric: true });
        });

        // 3. Build HTML with Hierarchy Awareness
        const subjectName = subjectMap[selectedSubject] || selectedSubject;
        const dateStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        const logoUrl = window.location.origin + '/logo.png'; 

        // Variable to track depth changes
        let previousPath = [];

        const renderHierarchy = (rule) => {
            const currentPath = rule.displayPath || [];
            let html = '';
            
            // Compare current path with previous path to insert headings
            // We iterate up to the length of currentPath
            currentPath.forEach((segment, index) => {
                // If this segment is different from the previous one at this index,
                // OR if the previous path didn't extend this far
                if (segment !== previousPath[index]) {
                    const depthClass = `heading-level-${index}`;
                    html += `<h${index + 2} class="hierarchy-heading ${depthClass}">${segment}</h${index + 2}>`;
                }
            });

            // Update tracker
            previousPath = [...currentPath];
            return html;
        };

        const htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>${subjectName} Outline - Wali's</title>
                <link rel="preconnect" href="https://fonts.googleapis.com">
                <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
                <link href="https://fonts.googleapis.com/css2?family=Manrope:wght@400;700;800&family=Source+Serif+4:ital,wght@0,400;0,600;1,400&display=swap" rel="stylesheet">
                <style>
                    :root {
                        --primary: #2E5175;
                        --text: #0D1C2E;
                        --muted: #525D73;
                        --bg-paper: #ffffff;
                        --font-heading: 'Manrope', sans-serif;
                        --font-body: 'Source Serif 4', serif;
                    }
                    body { 
                        font-family: var(--font-body); 
                        background-color: #f4f4f4; /* Presentation bg */
                        color: var(--text); 
                        line-height: 1.6; 
                        margin: 0;
                        padding: 40px;
                    }
                    .page-container {
                        background-color: var(--bg-paper);
                        max-width: 850px;
                        margin: 0 auto;
                        padding: 60px 80px;
                        box-shadow: 0 10px 30px rgba(0,0,0,0.05);
                        min-height: 100vh;
                    }
                    
                    /* Header */
                    header { 
                        text-align: center; 
                        border-bottom: 2px solid var(--text); 
                        padding-bottom: 40px; 
                        margin-bottom: 50px; 
                    }
                    .logo-img { width: 60px; height: 60px; margin-bottom: 15px; border-radius: 12px; }
                    .brand-name { 
                        font-family: var(--font-heading); 
                        font-weight: 800; 
                        font-size: 0.9rem; 
                        letter-spacing: 2px; 
                        color: var(--muted); 
                        text-transform: uppercase; 
                        margin-bottom: 5px;
                    }
                    h1 { 
                        font-family: var(--font-heading);
                        margin: 0; 
                        font-size: 3.5rem; 
                        text-transform: uppercase; 
                        letter-spacing: -1px; 
                        line-height: 1; 
                        color: var(--primary);
                    }
                    .meta { 
                        color: var(--muted); 
                        font-family: var(--font-heading); 
                        font-size: 0.85rem; 
                        margin-top: 15px; 
                        font-weight: 600;
                        text-transform: uppercase;
                        letter-spacing: 1px;
                    }

                    /* Hierarchy Headings */
                    .hierarchy-heading {
                        font-family: var(--font-heading);
                        color: var(--text);
                        margin-top: 40px;
                        margin-bottom: 15px;
                    }
                    
                    /* Level 0 (e.g. Intentional Torts) */
                    .heading-level-0 {
                        font-size: 1.4rem;
                        font-weight: 800;
                        text-transform: uppercase;
                        border-bottom: 1px solid #ddd;
                        padding-bottom: 8px;
                        color: var(--primary);
                        margin-top: 60px;
                    }
                    
                    /* Level 1 (e.g. Battery) */
                    .heading-level-1 {
                        font-size: 1.1rem;
                        font-weight: 700;
                        text-transform: uppercase;
                        color: var(--muted);
                        margin-left: 0px;
                    }

                    /* Level 2+ */
                    .heading-level-2, .heading-level-3 {
                        font-size: 1rem;
                        font-weight: 700;
                        font-style: italic;
                        color: var(--text);
                        margin-left: 20px;
                    }

                    /* Rule Block */
                    .rule-block {
                        margin-bottom: 25px;
                        margin-left: 20px; /* Indent rules slightly under headings */
                        page-break-inside: avoid;
                    }
                    
                    .rule-title {
                        font-family: var(--font-heading);
                        font-size: 1rem;
                        font-weight: 800;
                        color: var(--text);
                        margin: 0 0 4px 0;
                    }
                    
                    .keynote-text {
                        font-family: var(--font-body);
                        font-size: 1.1rem;
                        color: #333;
                        margin: 0;
                        line-height: 1.5;
                    }

                    @media print {
                        body { background-color: white; padding: 0; }
                        .page-container { box-shadow: none; padding: 0; margin: 0; max-width: 100%; }
                        .heading-level-0 { break-after: avoid; }
                    }
                </style>
            </head>
            <body>
                <div class="page-container">
                    <header>
                        <img src="${logoUrl}" alt="Logo" class="logo-img" />
                        <div class="brand-name">Wali's</div>
                        <h1>${subjectName}</h1>
                        <div class="meta">Keynote Outline • ${dateStr}</div>
                        <span style="font-family: 'Manrope', sans-serif; font-size: 0.7rem; color: #999; text-transform: uppercase; letter-spacing: 1px;">Your keynotes organized into an outline.</span>
                    </header>
                    <main>
                        ${exportRules.map(rule => {
                            const headingsHtml = renderHierarchy(rule);
                            // Adjust indentation based on hierarchy depth (max 3 levels deep visual)
                            const depth = Math.min((rule.displayPath?.length || 0), 4);
                            const indent = depth * 20; 
                            
                            return `
                                ${headingsHtml}
                                <div class="rule-block" style="margin-left: ${indent}px">
                                    <h4 class="rule-title">${rule.ruleName}</h4>
                                    <p class="keynote-text">${rule.keynote}</p>
                                </div>
                            `;
                        }).join('')}
                    </main>
                    <footer style="margin-top: 80px; text-align: center; border-top: 1px solid #eee; padding-top: 20px;">
                        <span style="font-family: 'Manrope', sans-serif; font-size: 0.7rem; color: #999; text-transform: uppercase; letter-spacing: 1px;">Generated by Wali's Method</span>
                    </footer>
                </div>
            </body>
            </html>
        `;

        const win = window.open('', '_blank');
        if (win) {
            win.document.write(htmlContent);
            win.document.close();
        } else {
            alert("Please allow popups to export your outline.");
        }
    };

    return (
        <div className="legal-command-complex">
            {/* 1. SEARCH BOX */}
            <div className="command-search-container">
                <form className="search-box-full" onSubmit={(e) => { e.preventDefault(); if (searchTerm) setIsSearchModalOpen(true); }}>
                    <input 
                        type="text" 
                        className="search-input" 
                        placeholder="Search your keynotes..." 
                        value={searchTerm} 
                        onChange={(e) => setSearchTerm(e.target.value)} 
                    />
                    <button type="submit" className="search-btn">
                        <span className="material-symbols-outlined">search</span>
                    </button>
                </form>
            </div>

            {/* 2. MY OUTLINES CARD */}
            <div className="tool-card outlines-card">
                <div className="tool-header">
                    <span className="material-symbols-outlined tool-icon">folder_open</span>
                    <span className="tool-label">My Outlines</span>
                </div>
                <div className="pills-container-scroll">
                    {subjectsWithKeynotes.length > 0 ? subjectsWithKeynotes.map(subj => (
                        <button 
                            key={subj} 
                            className="subject-chip" 
                            style={{ backgroundColor: `var(--subj-${subj.toLowerCase()})` }}
                            onClick={() => setSelectedSubject(subj)}
                        >
                            {subjectMap[subj] || subj}
                        </button>
                    )) : <div className="empty-pills">Add keynotes to rules to build your outline.</div>}
                </div>
            </div>

            {/* MODAL SYSTEM (PORTALED) */}
            {(isSearchModalOpen || selectedSubject) && ReactDOM.createPortal(
                <div className="modal-overlay active" onClick={() => { setIsSearchModalOpen(false); setSelectedSubject(null); }}>
                    <div className={`command-center-modal brutalist-modal theme-aware ${selectedSubject ? 'wide' : ''}`} onClick={e => e.stopPropagation()}>
                        <header className="modal-header style={{gap: '10px'}}">
                            <h2 className="modal-title">{selectedSubject ? `${subjectMap[selectedSubject]} Outline` : 'Search Results'}</h2>
                            <div style={{display:'flex', gap:'10px'}}>
                                {selectedSubject && (
                                    <button className="btn secondary small" onClick={handleExport} title="Export to HTML/PDF">
                                        Export
                                    </button>
                                )}
                                <button className="close-btn" style={{position: 'static'}} onClick={() => { setIsSearchModalOpen(false); setSelectedSubject(null); }}>&times;</button>
                            </div>
                        </header>
                        <div className="modal-body">
                            {selectedSubject ? (
                                outlineData.map(([unit, rules]) => (
                                    <div key={unit} className="outline-group">
                                        <div className="outline-unit">{unit}</div>
                                        {rules.map(r => (
                                            <div key={r.id} className="keynote-block" onClick={() => handleOpenRule(r.id)}>
                                                <span className="outline-path-crumb">{r.displayPath?.slice(1).join(' › ')}</span>
                                                <h3 className="keynote-rule-name">{r.ruleName}</h3>
                                                <div className="keynote-text">{r.keynote}</div>
                                            </div>
                                        ))}
                                    </div>
                                ))
                            ) : (
                                searchResults.length ? searchResults.map(res => (
                                    <div key={res.id} className="result-item" onClick={() => handleOpenRule(res.id)}>
                                        <span className="result-path">{subjectMap[res.subject]} › {res.displayPath?.join(' › ')}</span>
                                        <h3 className="keynote-rule-name">{res.ruleName}</h3>
                                        <div className="keynote-text" style={{fontSize: '0.9em', marginTop: '5px', paddingLeft: '0', borderLeft: 'none', color: 'var(--text-muted)'}}>
                                            {rulesProgress?.[res.id]?.keynote}
                                        </div>
                                    </div>
                                )) : <div className="no-results">No matches in your keynotes for "{searchTerm}"</div>
                            )}
                        </div>
                    </div>
                </div>,
                document.body 
            )}
        </div>
    );
};

export default LegalCommandWidget;